// src/components/settings/sections/ViewSettings.jsx

import React from 'react';
import { Eye, Settings, Shield, Globe, Users, Lock } from 'lucide-react';
import { FormField, Toggle, SelectInput } from '../shared/FormComponents';

export const ViewSettings = ({ formData, onInputChange }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">View Management</h3>
        <p className="text-sm text-gray-600">
          Configure how visitors and managers interact with your book
        </p>
      </div>

      {/* Enable Views */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-gray-200 rounded-lg p-4">
          <Toggle
            checked={formData.viewSettings?.enablePublicView !== false}
            onChange={(value) => onInputChange('viewSettings.enablePublicView', value)}
            label="Enable Public View"
            description="Allow visitors to see public content"
            icon={Eye}
          />
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <Toggle
            checked={formData.viewSettings?.enableManagementView !== false}
            onChange={(value) => onInputChange('viewSettings.enableManagementView', value)}
            label="Enable Management View"
            description="Provide management portal access"
            icon={Settings}
          />
        </div>
      </div>

      {/* View Labels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField label="Public View Label">
          <input
            type="text"
            value={formData.viewSettings?.publicViewLabel || 'Public View'}
            onChange={(e) => onInputChange('viewSettings.publicViewLabel', e.target.value)}
            placeholder="e.g., Public View, Visitor View"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </FormField>

        <FormField label="Management View Label">
          <input
            type="text"
            value={formData.viewSettings?.managementViewLabel || 'Management'}
            onChange={(e) => onInputChange('viewSettings.managementViewLabel', e.target.value)}
            placeholder="e.g., Management, Admin Portal"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </FormField>
      </div>

      {/* Default View */}
      <FormField label="Default View">
        <SelectInput
          value={formData.viewSettings?.defaultView || 'public'}
          onChange={(value) => onInputChange('viewSettings.defaultView', value)}
          options={[
            { value: 'public', label: 'Public View (Default)', icon: Eye },
            { value: 'management', label: 'Management View', icon: Settings }
          ]}
        />
        <p className="text-xs text-gray-500 mt-1">
          The view that loads by default when someone visits the book
        </p>
      </FormField>

      {/* Authentication Requirements */}
      <div className="border border-gray-200 rounded-lg p-4">
        <Toggle
          checked={formData.viewSettings?.requireAuth !== false}
          onChange={(value) => onInputChange('viewSettings.requireAuth', value)}
          label="Require Authentication for Management View"
          description="Users must sign in to access management features"
          icon={Shield}
        />
      </div>

      {/* Allowed Roles */}
      <FormField label="Management Access Roles">
        <div className="space-y-2">
          {['owner', 'admin', 'editor', 'viewer'].map(role => (
            <label key={role} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={(formData.viewSettings?.allowedRoles || ['owner', 'admin', 'editor']).includes(role)}
                onChange={(e) => {
                  const currentRoles = formData.viewSettings?.allowedRoles || ['owner', 'admin', 'editor'];
                  const newRoles = e.target.checked
                    ? [...currentRoles, role]
                    : currentRoles.filter(r => r !== role);
                  onInputChange('viewSettings.allowedRoles', newRoles);
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-900 capitalize">{role}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Select which roles can access the management view
        </p>
      </FormField>
    </div>
  );
};